import React, { useEffect, useMemo, useState } from 'react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';

import { DetailsSectionHeader } from './DetailsSection';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import axios from 'axios';
import moment from 'moment';
import type {
	MessageSequenceInstance,
	MessageSequence,
} from '@/types/messageSequence';
import { Button } from '@/components/ui/button';
import { Loader2, Square } from 'lucide-react';

const MessageSequences = ({ _customer }: { _customer: string }) => {
	const [isOpen, setIsOpen] = usePersistentOpen(
		`conversation-details:message-sequences`,
	);
	const [loading, setLoading] = useState(false);
	const [instances, setInstances] = useState<MessageSequenceInstance[]>([]);
	const [stoppingId, setStoppingId] = useState<string | null>(null);

	useEffect(() => {
		let mounted = true;
		const fetchInstances = async () => {
			try {
				setLoading(true);
				const { data } = await axios.get(
					`/api/message-sequences/instances/by-customer/${_customer}`,
				);
				if (mounted) setInstances(Array.isArray(data) ? data : []);
			} catch {
				// silently fail; global axios interceptor will toast
			} finally {
				if (mounted) setLoading(false);
			}
		};
		fetchInstances();
		const onRefresh = (e: CustomEvent<{ _customer?: string }> | Event) => {
			// Some environments may not type CustomEvent; perform a safe cast
			const ce = e as CustomEvent<{ _customer?: string }>;
			if (!ce?.detail || ce.detail._customer === _customer) {
				fetchInstances();
			}
		};
		window.addEventListener(
			'message-sequences:refresh',
			onRefresh as EventListener,
		);
		return () => {
			mounted = false;
			window.removeEventListener(
				'message-sequences:refresh',
				onRefresh as EventListener,
			);
		};
	}, [_customer]);

	const items = useMemo(() => {
		return (instances || []).map((inst) => {
			const seq = inst._messageSequence as MessageSequence;
			const steps = (seq && seq.steps) || [];
			const total = steps.length;
			const idx = Math.min(
				Math.max(inst.currentStepIndex || 0, 0),
				Math.max(total - 1, 0),
			);
			return { inst, seq, steps, total, idx };
		});
	}, [instances]);

	const stopInstance = async (id: string) => {
		try {
			setStoppingId(id);
			const { data } = await axios.patch(
				`/api/message-sequences/instances/${id}/stop`,
				{ reason: 'User stopped' },
			);
			setInstances((prev) =>
				prev.map((i) => (i._id === id ? { ...i, ...data } : i)),
			);
		} catch {
			// toast handled globally
		} finally {
			setStoppingId(null);
		}
	};

	return (
		<div className="border-b border-border/60">
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<DetailsSectionHeader className="w-full" title="Message sequences" />
				<CollapsibleContent>
					<div className="px-4 pb-3">
						{loading ? (
							<p className="text-sm text-muted-foreground">
								Loading…
							</p>
						) : items.length === 0 ? (
							<p className="text-sm text-muted-foreground">
								No active sequences. Add one in the chat window.
							</p>
						) : (
							<div className="flex flex-col gap-3">
								{items.map(
									({ inst, seq, steps, total, idx }) => {
										const statusClass =
											inst.status === 'completed'
												? 'border-green-300 text-green-700 bg-green-50'
												: inst.status === 'paused'
													? 'border-amber-300 text-amber-700 bg-amber-50'
													: inst.status === 'stopped'
														? 'border-red-300 text-red-700 bg-red-50'
														: inst.status ===
															  'stopped_by_reply'
															? 'border-blue-300 text-blue-700 bg-blue-50'
															: 'border-primary/30 text-primary bg-primary/10';

										const isCompleted =
											inst.status === 'completed';

										return (
											<div
												key={inst._id}
												className="rounded-md border p-3"
											>
												<div className="flex items-center justify-between gap-2">
													<div
														className="font-medium truncate"
														title={seq?.title || ''}
													>
														{seq?.title ||
															'Untitled sequence'}
													</div>
													<span
														className={`px-2 py-0.5 rounded-full text-xs border capitalize ${statusClass}`}
													>
														{inst.status.replaceAll(
															'_',
															' ',
														)}
													</span>
												</div>

												{isCompleted ? (
													// Simplified view for completed sequences
													<div className="mt-2">
														{inst.completedAt && (
															<div className="text-xs text-muted-foreground">
																Completed{' '}
																{moment(
																	inst.completedAt,
																).format(
																	'll',
																)}
															</div>
														)}
													</div>
												) : (
													// Full details for active/paused/stopped sequences
													<div className="mt-2 space-y-2">
														<div className="flex items-center justify-between text-xs text-muted-foreground">
															<div className="text-xs text-muted-foreground">
																Next send:{' '}
																{inst.nextScheduledAt
																	? moment(
																			inst.nextScheduledAt,
																		).format(
																			'll LT',
																		)
																	: '—'}
															</div>

															<span>
																Step{' '}
																{Math.min(
																	idx + 1,
																	total,
																)}
																/{total}
															</span>
														</div>

														<div className="mt-1">
															<div className="text-xs text-muted-foreground">
																Next message:
															</div>
															<div className="text-sm line-clamp-2">
																{steps[idx]
																	?.text ||
																	'—'}
															</div>
														</div>
														<div className="mt-2 flex flex-wrap gap-1 items-center">
															{steps.map(
																(
																	step,
																	sidx,
																) => {
																	const state =
																		sidx <
																		idx
																			? 'done'
																			: sidx ===
																				  idx
																				? 'current'
																				: 'next';
																	return (
																		<span
																			key={
																				sidx
																			}
																			className={
																				`px-2 py-0.5 rounded-full text-xs border ` +
																				(state ===
																				'done'
																					? 'bg-muted text-muted-foreground'
																					: state ===
																						  'current'
																						? 'bg-primary/10 border-primary/30 text-primary'
																						: 'bg-background text-muted-foreground')
																			}
																			title={
																				step.text
																			}
																		>
																			{sidx +
																				1}
																		</span>
																	);
																},
															)}
															{inst.status ===
																'active' && (
																<Button
																	variant="ghost"
																	size="sm"
																	className="ml-auto h-7 gap-1 max-md:min-h-11 max-md:px-3 max-md:text-sm"
																	onClick={() =>
																		stopInstance(
																			inst._id,
																		)
																	}
																	disabled={
																		stoppingId ===
																		inst._id
																	}
																>
																	{stoppingId ===
																	inst._id ? (
																		<Loader2 className="h-3.5 w-3.5 animate-spin" />
																	) : (
																		<Square className="h-3.5 w-3.5" />
																	)}
																	Stop
																</Button>
															)}
														</div>
													</div>
												)}
											</div>
										);
									},
								)}
							</div>
						)}
					</div>
				</CollapsibleContent>
			</Collapsible>
		</div>
	);
};

export default MessageSequences;
